import "./TableView.css"

import { useState, useEffect } from "react"

import {
  DataTable,
  ExportButton,
  FavouriteSwitch,
  IconButton,
  SelectMenu,
  TablePageSelector,
  isModalEditable,
  openEditModal,
} from "web/components"

import { useSetting } from "web/hooks/data"
import { useLockedFieldEdit } from "web/hooks/modals"
import useAsyncError from "web/hooks/behaviour/useAsyncError"
import { NavigationProvider } from "web/contexts"
import { isObjectWithProperty } from "web/services/helpers"

import TableSettingsButton from "./TableSettingsButton"
import EditableCell from "./EditableCell"
import ScheduledExportButton from "./ScheduledExport/ScheduledExportButton"

const ROWS_PER_PAGE_OPTIONS = [
  { label: "50 Rows", value: 50 },
  { label: "100 Rows", value: 100 },
  { label: "150 Rows", value: 150 },
  { label: "200 Rows", value: 200 },
]

export default function TableView({
  subjectType,
  subjectId,
  parentSubjectType,
  parentSubjectId,
  topic,
  data,
  searchQuery,
  setSearchQuery,
  groupCollection,
  groupTotals,
  grandTotals,
  stickyGroupTotals,
  stickyGrandTotals,
  fields: viewFields,
  enableFavourites,
  page,
  pageCount,
  recordCount,
  rowsPerPage,
  handlePageChanged,
  view: viewable,
  exportPath,
  listType,
  enableScheduledExport,
  enableExport,
  enableEdit,
  enableFieldSelection = true,
  onSort,
  onGroup,
  onRowsPerPageChanged,
  onSelectionChanged,
  onSelectAllChanged,
  onFrozenFieldChanged,
  onRowClick,
  emptyState,
  showRowNumbers,
}) {
  const [inEditMode, setInEditMode] = useState(false)
  const throwError = useAsyncError()

  const [collapsedGroups, setGroupCollapsedState] = useSetting({
    scope: "user",
    setting: `group-${listType}-${viewable.type}-${viewable.id}`,
    defaultValue: [],
  })

  const hasTopic = !!topic
  const hasData = !!data.length

  const itemLabel = (hasTopic && topic.name) || subjectType

  function getTableColumns() {
    const columns = []

    const mustDisplayDetails =
      !viewable.groupField || !viewable.table.show_group_totals
    const displayingDetails = mustDisplayDetails || viewable.table.show_details

    if (enableFavourites && displayingDetails) {
      columns.push({
        type: "component",
        component: FavouriteSwitch,
        style: {
          width: "30px",
          padding: "0px",
          justifyContent: "center",
          alignItems: "center",
        },
        width: 30,
      })
    }

    viewFields.forEach(
      ({ id, column_width, field, is_editable, is_frozen, presentation }) => {
        const column = {
          viewFieldId: id,
          type: "data",
          label: field.label,
          fieldId: field.id,
          fieldKind: field.kind,
          fieldFormat: field.format,
          dataKey: field.key,
          width: column_width,
          isEditable: is_editable,
          isFrozen: is_frozen,
          presentation: presentation,
        }

        columns.push(column)
      }
    )

    return columns
  }

  async function handleMoveColumn(fromColumn, toColumn) {
    const fromIndex =
      viewFields.findIndex((el) => el.field.key === fromColumn.dataKey) + 1
    const toIndex =
      viewFields.findIndex((el) => el.field.key === toColumn.dataKey) + 1
    try {
      await viewFields.reorder(
        fromIndex,
        toIndex > fromIndex ? toIndex - 1 : toIndex
      )
    } catch (error) {
      throwError(error)
    }
  }

  function handleResizeColumn(column, width) {
    viewable.fields.updateColumnWidth(column.viewFieldId, width)
  }

  const permissionedData = data.map((el) => ({
    ...el,
    isEditable:
      isObjectWithProperty(el, "user_permissions") &&
      el.user_permissions.update,
    readonlyFields: el.readonly_fields,
  }))

  const columns = getTableColumns()

  function EditCell({ rowData, column }) {
    const viewField = viewFields.find((vf) => vf.field.key === column.dataKey)
    return (
      <EditableCell
        subjectType={subjectType}
        subjectId={subjectId}
        topic={topic}
        rowData={rowData}
        field={viewField.field}
      />
    )
  }

  const lockedFieldEditModal = useLockedFieldEdit()

  function openLockedFieldEditModal(fieldKey, rowData) {
    return lockedFieldEditModal(fieldKey, fieldForKey(fieldKey), rowData, null)
  }

  function fieldForKey(key) {
    const viewField = viewFields.find(
      (viewField) => viewField.field.key === key
    )

    return viewField.field
  }

  function wrappedIsModalEditable(fieldKey, data) {
    return isModalEditable(fieldForKey(fieldKey), data)
  }

  function wrappedOpenEditModal(fieldKey, data) {
    return openEditModal(fieldForKey(fieldKey), data, "table")
  }

  useEffect(() => {
    return () => {
      setSearchQuery("")
    }
  }, [setSearchQuery])

  return (
    <div className="flex min-h-0 min-w-0 grow flex-col">
      <div className="mb-2 flex items-center gap-x-2 pr-3 md:pr-5">
        <input
          className="h-8 min-w-0 flex-auto rounded-md border border-neutral-8 p-3 text-sm focus:outline-none"
          value={searchQuery}
          placeholder="Search..."
          onChange={(e) => {
            setSearchQuery(e.target.value)
            handlePageChanged(1)
          }}
        />
        <div className="h-8 w-28 shrink-0 rounded-md border border-neutral-8 bg-white text-sm">
          <SelectMenu
            selectedValue={ROWS_PER_PAGE_OPTIONS.find(
              (o) => o.value === rowsPerPage
            )}
            options={ROWS_PER_PAGE_OPTIONS}
            handleSelectChange={(option) => onRowsPerPageChanged(option.value)}
          />
        </div>
        <TablePageSelector
          currentPage={page}
          numberOfPages={pageCount}
          numberOfRecords={recordCount}
          recordsPerPage={rowsPerPage}
          onPageChange={handlePageChanged}
          hasData={hasData}
        />
        {enableEdit && (
          <IconButton
            name="pencil"
            outline
            onClick={() => setInEditMode(!inEditMode)}
            active={inEditMode}
            disabled={!hasData}
          />
        )}
        {enableScheduledExport && (
          <ScheduledExportButton
            viewId={viewable.id}
            viewType={viewable.type}
          />
        )}
        {enableExport && (
          <ExportButton
            exportPath={exportPath}
            parentSubjectType={parentSubjectType}
            parentSubjectId={parentSubjectId}
            subjectType={subjectType}
            subjectId={subjectId}
            topicId={topic && topic.id}
            view={viewable}
            listType={listType}
          />
        )}
        <TableSettingsButton
          viewId={viewable.id}
          viewType={viewable.type}
          enableFieldSelection={enableFieldSelection}
        />
      </div>
      <NavigationProvider subjectType={subjectType}>
        <DataTable
          columns={columns}
          data={permissionedData}
          groupCollection={groupCollection}
          pageNumber={page}
          groupTotals={groupTotals}
          grandTotals={grandTotals}
          stickyGroupTotals={stickyGroupTotals}
          stickyGrandTotals={stickyGrandTotals}
          onSort={onSort}
          onFrozenFieldChanged={onFrozenFieldChanged}
          onGroup={onGroup}
          onGroupCollapseStateChanged={setGroupCollapsedState}
          sortColumn={viewable.sortField}
          sortDesc={viewable.sortDesc}
          enableFrozenColumns
          groupColumn={viewable.groupField}
          groupDesc={viewable.groupDesc}
          secondGroupColumn={viewable.secondGroupField}
          secondGroupDesc={viewable.secondGroupDesc}
          collapsedGroups={collapsedGroups}
          onRowClick={onRowClick}
          onRowSelectionChanged={onSelectionChanged}
          onSelectAllChanged={onSelectAllChanged}
          onMoveColumn={handleMoveColumn}
          onResizeColumn={handleResizeColumn}
          itemLabel={itemLabel}
          renderToEditCell={EditCell}
          isEditable={inEditMode}
          showGroupTotals={viewable.table.show_group_totals}
          showGrandTotals={viewable.table.show_grand_totals}
          showDetails={viewable.table.show_details}
          openLockedFieldEditModal={openLockedFieldEditModal}
          isModalEditable={wrappedIsModalEditable}
          openEditModal={wrappedOpenEditModal}
          showRowNumbers={showRowNumbers}
        />
      </NavigationProvider>
      {!hasData && emptyState}
    </div>
  )
}
